import { EARLY_CAREER_GRAD_YEAR, getQuestEndDate } from '../config/recruitingCycle';

// Weekly LeetCode target, ramping with the application season and capped by
// however many hours the user said they can give.
const leetcodeGoalFor = (date, hoursPerWeek) => {
  const month = date.getMonth();
  let goal;
  switch (month) {
    case 5: // June
      goal = 5;
      break;
    case 6: // July
      goal = 7;
      break;
    case 7: // August
      goal = 6;
      break;
    case 8: // September
      goal = 5;
      break;
    case 9: // October
      goal = 4;
      break;
    default:
      goal = 1;
  }
  return Math.min(goal, hoursPerWeek);
};

// Weekly application target. The youngest cohort applies later and to fewer
// roles, since underclassman postings open further into the season.
const appGoalFor = (date, gradYear) => {
  const month = date.getMonth();

  if (gradYear === EARLY_CAREER_GRAD_YEAR) {
    if (month < 6) return 0; // before July
    if (month === 6) return 2; // July
    return 4; // after July
  }

  switch (month) {
    case 5: // June
      return 1;
    case 6: // July
      return 3;
    case 7: // August
    case 8: // September
    case 9: // October
    case 10: // November
      return 7;
    case 11: // December
      return 3;
    default:
      return 2;
  }
};

/**
 * Build a weekly quest plan running from the coming Sunday to the end of the
 * current recruiting cycle.
 *
 * Extracted from AcceptQuestModal so that renewing a quest for a new cycle
 * produces exactly the same shape as accepting one for the first time -- two
 * copies of this would drift.
 *
 * Returns an empty array if the cycle has already ended, which is what made the
 * Summer 2026 plans generate empty. Callers should treat that as an error
 * rather than saving it.
 */
export const buildQuestPlan = ({ gradYear, hoursPerWeek, nodes = {}, completedNodes = [] }) => {
  const questPlan = [];
  const currentDate = new Date();

  const nextSunday = new Date(currentDate);
  nextSunday.setDate(currentDate.getDate() + ((7 - currentDate.getDay()) % 7));
  nextSunday.setHours(0, 0, 0, 0);

  const endDate = getQuestEndDate(gradYear);

  const currentWeek = new Date(nextSunday);
  let weekCount = 0;

  while (currentWeek <= endDate) {
    const tasks = {
      lessons: [],
      resume: [],
      leetcode: Array(leetcodeGoalFor(currentWeek, hoursPerWeek))
        .fill()
        .map(() => ({ problem_name: '', completed: false })),
      apps: Array(appGoalFor(currentWeek, gradYear))
        .fill()
        .map(() => ({ company_name: '', completed: false })),
    };

    if (weekCount === 0) {
      tasks.lessons = Object.values(nodes)
        .filter((node) => node.id !== 'start')
        .map((node) => ({
          lesson_name: node.title,
          completed: completedNodes.includes(node.id),
        }));
    } else if (weekCount === 1) {
      tasks.resume = [
        { task_name: 'Create resume draft', completed: false },
        { task_name: 'Have one person review your resume', completed: false },
        { task_name: 'Finalize resume', completed: false },
      ];
    }

    questPlan.push({ week_start: new Date(currentWeek), tasks });

    currentWeek.setDate(currentWeek.getDate() + 7);
    weekCount++;
  }

  return questPlan;
};

/**
 * Whether a stored quest belongs to a finished cycle and should be renewed.
 *
 * There is no cycle marker on a quest, so staleness is read off the plan: a
 * quest whose last week has passed can no longer show the user anything current
 * (QuestMiddleColumn falls back to week 1 forever). An empty plan counts too --
 * those were saved during the window when generation produced nothing.
 */
export const isQuestStale = (quest) => {
  const plan = quest?.quest_plan;
  if (!Array.isArray(plan)) return false;
  if (plan.length === 0) return true;

  const lastWeek = new Date(plan[plan.length - 1].week_start);
  if (Number.isNaN(lastWeek.getTime())) return false;

  const endOfLastWeek = new Date(lastWeek);
  endOfLastWeek.setDate(endOfLastWeek.getDate() + 7);
  return endOfLastWeek < new Date();
};
